import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import apiClient from "../apiClient";
import { formatMoney } from "../formatMoney";

const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [periods, setPeriods] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "Date", direction: "asc" });
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [savingTransactionId, setSavingTransactionId] = useState("");
  const [error, setError] = useState("");
  const requestedPeriodId = searchParams.get("periodId");

  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => (a.Description || "").localeCompare(b.Description || "")),
    [periods]
  );

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [periodsRes, currenciesRes, subcategoriesRes] = await Promise.all([
          apiClient.get("/periods"),
          apiClient.get("/currencies"),
          apiClient.get("/subcategories"),
        ]);
        const rows = Array.isArray(periodsRes.data) ? periodsRes.data : [];
        const currencyRows = Array.isArray(currenciesRes.data) ? currenciesRes.data : [];
        const subcategoryRows = Array.isArray(subcategoriesRes.data) ? subcategoriesRes.data : [];
        setPeriods(rows);
        setSubcategories(subcategoryRows);
        setDefaultCurrency(currencyRows.find((row) => Number(row.IsDefault))?.Description || "");
      } catch (err) {
        setError("Failed to load periods.");
      } finally {
        setLoadingPeriods(false);
      }
    };

    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (periods.length === 0) return;
    const requestedPeriod = periods.find((row) => String(row.PeriodId) === String(requestedPeriodId));
    const nextPeriodId = String(requestedPeriod?.PeriodId || periods[0].PeriodId);
    setPeriodId((current) => (current === nextPeriodId ? current : nextPeriodId));
  }, [periods, requestedPeriodId]);

  useEffect(() => {
    if (!periodId) return;
    if (String(requestedPeriodId) === String(periodId)) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("periodId", String(periodId));
      return next;
    }, { replace: true });
  }, [periodId, requestedPeriodId, setSearchParams]);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!periodId) {
        setTransactions([]);
        return;
      }

      setLoadingTransactions(true);
      setError("");
      try {
        const res = await apiClient.get("/transactions", { params: { periodId } });
        setTransactions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setError("Failed to load transactions for this period.");
        setTransactions([]);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [periodId]);

  const categoryOptions = useMemo(
    () => Array.from(new Set(transactions.map((row) => row.Category).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [transactions]
  );

  const sortedSubcategories = useMemo(
    () => [...subcategories].sort((a, b) => (a.SubcategoryDescription || "").localeCompare(b.SubcategoryDescription || "")),
    [subcategories]
  );

  const subcategoryOptionsByTransaction = useMemo(() => (
    Object.fromEntries(
      transactions.map((row) => {
        const hasCurrentOption = sortedSubcategories.some(
          (item) => String(item.SubcategoryId) === String(row.SubcategoryId)
        );
        if (hasCurrentOption || !row.SubcategoryId) {
          return [row.TransactionID, sortedSubcategories];
        }
        return [
          row.TransactionID,
          [
            ...sortedSubcategories,
            {
              SubcategoryId: row.SubcategoryId,
              SubcategoryDescription: row.Subcategory || "Unknown subcategory",
              CategoryDescription: row.Category || "",
            },
          ].sort((a, b) => (a.SubcategoryDescription || "").localeCompare(b.SubcategoryDescription || "")),
        ];
      })
    )
  ), [transactions, sortedSubcategories]);

  const filteredTransactions = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = transactions.filter((row) => {
      const matchesSearch = !query || [row.Date, row.Description, row.TransactionType, row.Category, row.Subcategory]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
      const matchesCategory = categoryFilter === "all" || row.Category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
    return [...filtered].sort((left, right) => {
      const leftValue = left[sortConfig.key];
      const rightValue = right[sortConfig.key];
      const leftNumeric = Number(leftValue);
      const rightNumeric = Number(rightValue);
      const comparison = Number.isFinite(leftNumeric) && Number.isFinite(rightNumeric)
        ? leftNumeric - rightNumeric
        : String(leftValue || "").localeCompare(String(rightValue || ""));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [transactions, search, categoryFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => (
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: key === "Date" ? "asc" : "asc" }
    ));
  };

  const selectedPeriod = useMemo(
    () => periods.find((period) => String(period.PeriodId) === String(periodId)) || null,
    [periods, periodId]
  );

  const summary = useMemo(() => {
    const totals = filteredTransactions.reduce((acc, row) => {
      const amount = Number(row.Amount || 0);
      if (amount >= 0) {
        acc.totalCredits += amount;
      } else {
        acc.totalDebits += Math.abs(amount);
      }
      return acc;
    }, { totalCredits: 0, totalDebits: 0 });
    return {
      count: filteredTransactions.length,
      totalCredits: totals.totalCredits,
      totalDebits: totals.totalDebits,
    };
  }, [filteredTransactions]);

  const formatTransactionAmount = (row) => {
    const numericAmount = Number(row.Amount || 0);
    const transactionType = String(row.TransactionType || "").trim().toLowerCase();
    const displayAmount = formatMoney(Math.abs(numericAmount), defaultCurrency);
    return transactionType === "debit" ? `- ${displayAmount}` : displayAmount;
  };

  const handleInlineSubcategoryChange = async (transactionId, nextSubcategoryId) => {
    const currentRow = transactions.find((row) => String(row.TransactionID) === String(transactionId));
    if (!currentRow || !nextSubcategoryId || String(currentRow.SubcategoryId) === String(nextSubcategoryId)) return;

    setSavingTransactionId(String(transactionId));
    setError("");
    try {
      await apiClient.put(`/transactions/${transactionId}`, {
        Description: currentRow.Description,
        Amount: Number(currentRow.Amount),
        TransactionTypeID: currentRow.TransactionTypeID,
        SubcategoryId: Number(nextSubcategoryId),
        Date: currentRow.Date,
        PeriodId: currentRow.PeriodId,
      });

      const selectedSubcategory = sortedSubcategories.find(
        (item) => String(item.SubcategoryId) === String(nextSubcategoryId)
      );

      setTransactions((current) => current.map((row) => (
        String(row.TransactionID) === String(transactionId)
          ? {
              ...row,
              SubcategoryId: Number(nextSubcategoryId),
              Subcategory: selectedSubcategory?.SubcategoryDescription || row.Subcategory,
              Category: selectedSubcategory?.CategoryDescription || row.Category,
            }
          : row
      )));
    } catch (err) {
      setError("Could not update transaction subcategory. Please try again.");
    } finally {
      setSavingTransactionId("");
    }
  };

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Transactions
          </p>
        </div>
        <Link className="btn-link" to="/TransactionsAdd">
          Add Transaction
        </Link>
      </header>

      <section className="panel form-card list-filter-panel">
        <div className="list-filters">
          <label className="list-filter-field list-filter-field-compact" htmlFor="transactions-period">
            <span>Period</span>
            <select
              id="transactions-period"
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              disabled={loadingPeriods}
            >
              {sortedPeriods.map((period) => (
                <option key={period.PeriodId} value={period.PeriodId}>
                  {period.Description}
                </option>
              ))}
            </select>
          </label>
          <label className="list-filter-field" htmlFor="transactions-search">
            <span>Search</span>
            <input
              id="transactions-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a transaction"
            />
          </label>
          <label className="list-filter-field list-filter-field-compact" htmlFor="transactions-category">
            <span>Category</span>
            <select
              id="transactions-category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {error && <p className="notice notice-error page-cta">{error}</p>}
      {(loadingPeriods || loadingTransactions) && (
        <p className="notice notice-muted page-cta">Loading transactions...</p>
      )}

      {!loadingPeriods && !loadingTransactions && (
        <section className="panel page-cta">
          <div className="dashboard-grid report-summary-strip">
            <div className="metric-card metric-card-compact">
              <strong>Selected Period</strong>
              <div className="resource-meta">{selectedPeriod?.Description || "-"}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Transactions</strong>
              <div className="resource-meta">{summary.count}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Total Credits</strong>
              <div className="resource-meta">{formatMoney(summary.totalCredits, defaultCurrency)}</div>
            </div>
            <div className="metric-card metric-card-compact">
              <strong>Total Debits</strong>
              <div className="resource-meta">{formatMoney(summary.totalDebits, defaultCurrency)}</div>
            </div>
          </div>
          <h2>Transactions In Selected Period</h2>
          {filteredTransactions.length === 0 ? (
            <p className="notice notice-muted">No transactions found for this period.</p>
          ) : (
            <div className="table-scroll">
              <table className="report-table">
                <thead>
                  <tr>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Date")}>Date</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Description")}>Description</button></th>
                    <th className="table-number"><button type="button" className="table-sort table-sort-number" onClick={() => handleSort("Amount")}>Amount</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("TransactionType")}>Type</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Subcategory")}>Subcategory</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("Category")}>Category</button></th>
                    <th><button type="button" className="table-sort" onClick={() => handleSort("EnteredByUserName")}>Entered By</button></th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((row) => (
                    <tr key={row.TransactionID}>
                      <td>{row.Date}</td>
                      <td>{row.Description}</td>
                      <td className="table-number table-money">{formatTransactionAmount(row)}</td>
                      <td>{row.TransactionType || "-"}</td>
                      <td>
                        <select
                          value={row.SubcategoryId || ""}
                          onChange={(e) => handleInlineSubcategoryChange(row.TransactionID, e.target.value)}
                          disabled={savingTransactionId === String(row.TransactionID)}
                        >
                          {(subcategoryOptionsByTransaction[row.TransactionID] || sortedSubcategories).map((item) => (
                            <option key={item.SubcategoryId} value={item.SubcategoryId}>
                              {item.SubcategoryDescription}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>{row.Category || "-"}</td>
                      <td>{row.EnteredByUserName || "-"}</td>
                      <td>
                        {savingTransactionId === String(row.TransactionID) ? "Saving..." : (
                          <Link className="btn-link" to={`/TransactionsUpdate/${row.TransactionID}`}>
                            Edit
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Transactions;
